// The tower shot recipes, mockup round 050 (R1007).
//
// One entry per look id of `labels.toml shot_sentinel_styles`, keyed by the
// id. The tower role fires these (R1009). The rules of the file are the rules
// of `combat-recipes-melee.ts`.
//
// **WHERE THE MUZZLE STANDS.** On the mockup page `from` is the tip of the
// Sentinel needle, 14 px ahead of the hull centre, and a card that draws on
// the anchor ring builds the hull centre itself as `from − 14 · u`. On the map
// the layer hands the recipe the SHOOTER'S OWN POINT as `c.from`
// (`CombatLayer.fire` writes `ev.sourceX`), which is the hull centre. The two
// points of the card are therefore ONE point here, and that is how the AUTO
// look already reads it: `c.from` is the muzzle AND the centre of the anchor
// ring, and the card's `d.len + 14` becomes `c.d.len`. Nothing else of the
// geometry moves.
//
// **A recipe trusts the layer for the flight** (R1367). The layer lands a
// round at `p = 1` and never packs one before it left, so every recipe here
// reads `p` in `[0, 1)` and holds no guard of its own, as the melee and the
// artillery recipes do.
//
// This is a leaf (`leaves.test.ts`): it imports the kit and the generated
// pack, and never an engine.

import {
  TAU, COMBAT_WHITE, DEEP_INK, NEUTRAL_INK, PALE_INK,
  arcOf, atOn, blob, dart, disc, flare, hash, hexOutline, line, out, ring,
  trail,
  type Pt, type ShotRecipeFn,
} from './combat-recipe-kit';

// ---------------------------------------------------------------------------
// The proportions
// ---------------------------------------------------------------------------

/** charge-and-snap, card 11: where the charge ends and the beam snaps. */
const SNAP_AT = 0.82;
/** charge-and-snap: the aim line's alpha at the start and its whole rise. */
const CHARGE_AIM_REST = 0.08, CHARGE_AIM_RISE = 0.14;
/** charge-and-snap: the muzzle bloom's floor radius, as a share of its reach. */
const CHARGE_BLOOM_FLOOR = 0.3;
/** charge-and-snap: the muzzle bloom's alpha at rest and its whole rise. */
const CHARGE_BLOOM_REST = 0.3, CHARGE_BLOOM_RISE = 0.5;
/** charge-and-snap: the core pip's floor radius and its whole growth, in px. */
const CHARGE_CORE_FLOOR = 1, CHARGE_CORE_RISE = 2;
/** charge-and-snap: the core pip's alpha, which holds through the charge. */
const CHARGE_CORE_ALPHA = 0.9;
/** charge-and-snap: how far the beam fades over the snap. Half, so the beam
 *  is still there when the round lands. */
const SNAP_FADE = 0.5;
/** charge-and-snap: the beam band's floor width, the card's `1 +`. */
const SNAP_BAND_FLOOR = 1;

/** sentry-pulses, card 01: how much of its alpha a pulse loses over the
 *  flight. Half, so the ring is still a ring when it arrives. */
const SENTRY_FADE = 0.5;

/** bulwark-lattice, card 02: how far behind the round the ghost lattice
 *  stands, as a share of the flight. */
const BULWARK_LAG = 0.06;
/** bulwark-lattice: how faint the ghost lattice stands under the leading
 *  one. A third, which is the card's 0.2 against its 0.6. */
const BULWARK_GHOST_K = 1 / 3;

/** interceptor-thread, card 03: the dash pattern of the thread, in world px.
 *  A dash is the shape of a line and not a figure a designer moves. */
const THREAD_DASH_ON = 2, THREAD_DASH_OFF = 5;

/** wind-up, card 06: where the circle closes and the round leaves. */
const WINDUP_CLOSE_AT = 0.7;
/** wind-up: how faint the guide ring stands under the winding arc. */
const WINDUP_GUIDE_ALPHA = 0.2;
/** wind-up: where in the wind the muzzle blooms, and how bright it reaches. */
const WINDUP_FLASH_AT = 0.9, WINDUP_FLASH_ALPHA = 0.8;
/** wind-up: how far the ring swells as the round leaves, as a share of its
 *  own radius, and how bright it stands at the moment it swells. */
const WINDUP_SWELL_K = 0.42, WINDUP_RELEASE_ALPHA = 0.5;
/** wind-up: how much heavier the swelling ring strokes than the guide ring. */
const WINDUP_RELEASE_W_K = 1.5;
/** wind-up: the trail's stroke, as a share of the winding arc's, and its
 *  alpha. */
const WINDUP_TRAIL_W_K = 0.8, WINDUP_TRAIL_ALPHA = 0.5;

/** tripwire, card 12: where the wire is laid and the bead starts to run. */
const TRIPWIRE_LAY_AT = 0.4;
/** tripwire: the dash pattern of the wire, in world px. */
const TRIPWIRE_DASH_ON = 2, TRIPWIRE_DASH_OFF = 3;
/** tripwire: the alpha of the bead that lays the wire. */
const TRIPWIRE_TIP_ALPHA = 0.9;
/** tripwire: how much of its alpha the wire keeps once the bead runs it, and
 *  how much of THAT it then loses. */
const TRIPWIRE_RUN_K = 5 / 6, TRIPWIRE_FADE = 0.6;
/** tripwire: the white lead the bead lays over the wire behind it. */
const TRIPWIRE_LEAD_ALPHA = 0.35;

/** drum-fire, card 14: how much of the flight the muzzle bar shows for, and
 *  how bright it flashes. */
const DRUM_BAR_SPAN = 0.15, DRUM_BAR_ALPHA = 0.9;

/** stepped-bolt, card 16: how bright a ghost stands behind the head, and how
 *  bright the link between the last two steps stands. */
const BOLT_GHOST_ALPHA = 0.5, BOLT_LINK_ALPHA = 0.25;

/** mine-drift, card 17: how much of the glide the ease takes; the rest runs
 *  flat, so the mine never stops dead. */
const MINE_EASE_K = 0.85;
/** mine-drift: how many turns the wobble takes over the whole drift. */
const MINE_WOBBLE_TURNS = 2.2;
/** mine-drift: where the shadow hangs and how wide it lies, as shares of the
 *  body's own radius, and how dark it stands. */
const MINE_SHADOW_DX_K = 1 / 3, MINE_SHADOW_DY_K = 1;
const MINE_SHADOW_RX_K = 4 / 3, MINE_SHADOW_RY_K = 2 / 3;
const MINE_SHADOW_ALPHA = 0.4;
/** mine-drift: the blinking pip, as a share of the body, its alpha at rest,
 *  its whole rise, and the cadence of the blink in radians a second. */
const MINE_PIP_K = 1 / 3;
const MINE_BLINK_REST = 0.4, MINE_BLINK_RISE = 0.6, MINE_BLINK_RATE = 4;

/** sea-bed-kick, card 20: how much of the flight the silt puff shows for. */
const KICK_PUFF_SPAN = 0.5;
/** sea-bed-kick: the puff's half height at birth and how far it grows across
 *  and up, as shares of its half width. */
const KICK_PUFF_RY_K = 0.5, KICK_PUFF_GROW_X_K = 1.25;
/** sea-bed-kick: how far the grains scatter across the puff and how far they
 *  rise, as shares of its half width. */
const KICK_GRAIN_SPREAD_K = 2, KICK_GRAIN_RISE_K = 1.75;
/** sea-bed-kick: a grain's smallest radius and the span above it, in world
 *  px, and how bright a grain stands. A grain is the grain of the sea bed and
 *  not a figure a designer moves, and the row holds no slot for it. */
const KICK_GRAIN_R = 0.8, KICK_GRAIN_R_SPAN = 1, KICK_GRAIN_ALPHA = 0.6;
/** sea-bed-kick: the bead's trail and its white core. */
const KICK_TRAIL_ALPHA = 0.35, KICK_CORE_ALPHA = 0.85;

/** The two reused points of this file (R131). */
const P0: Pt = { x: 0, y: 0 };
const P1: Pt = { x: 0, y: 0 };

export const RECIPES: Readonly<Record<string, ShotRecipeFn>> = {
  /**
   * Card 11, CHARGE AND SNAP (the AUTO look).
   *
   * A beam that charges, then snaps. A glow builds on the muzzle while sparks
   * spiral into it and the aim line thickens; at four fifths of the flight the
   * beam snaps to the target, white over the faction ink, and holds until the
   * hit.
   */
  'charge-and-snap'(c) {
    const r = c.row;
    if (c.p < SNAP_AT) {
      const u = c.p / SNAP_AT;
      line(
        c, c.from.x, c.from.y, c.to.x, c.to.y, r.width2Px, c.ink,
        CHARGE_AIM_REST + CHARGE_AIM_RISE * u,
      );
      for (let i = 0; i < r.count; i++) {
        const a = c.t * TAU * r.speed + (i / r.count) * TAU;
        const d = r.r2Px * (1 - u);
        disc(
          c, c.from.x + Math.cos(a) * d, c.from.y + Math.sin(a) * d,
          r.length2Px, c.ink, r.alpha2,
        );
      }
      flare(
        c, c.from.x, c.from.y, CHARGE_BLOOM_FLOOR * r.lengthPx + r.lengthPx * u,
        c.ink, CHARGE_BLOOM_REST + CHARGE_BLOOM_RISE * u,
      );
      disc(
        c, c.from.x, c.from.y, CHARGE_CORE_FLOOR + CHARGE_CORE_RISE * u,
        COMBAT_WHITE, CHARGE_CORE_ALPHA,
      );
      return;
    }
    const u = (c.p - SNAP_AT) / (1 - SNAP_AT);
    const f = 1 - SNAP_FADE * u;
    line(
      c, c.from.x, c.from.y, c.to.x, c.to.y, SNAP_BAND_FLOOR + r.widthPx * f,
      c.ink, r.alpha2 * f,
    );
    line(c, c.from.x, c.from.y, c.to.x, c.to.y, r.width2Px, COMBAT_WHITE, r.alpha * f);
    flare(c, c.from.x, c.from.y, r.lengthPx * f, c.ink, r.alpha2 * f);
    disc(c, c.to.x, c.to.y, r.rPx * f, COMBAT_WHITE, CHARGE_CORE_ALPHA);
  },

  /**
   * Card 01, SENTRY PULSES.
   *
   * Four rings leave the tower in a row, and each one grows a little as it
   * flies. The tower gun drawn as pressure and not as metal.
   */
  'sentry-pulses'(c) {
    const r = c.row;
    const h = atOn(c.d, c.from, c.p, 0, P0);
    ring(
      c, h.x, h.y, r.rPx + r.lengthPx * c.p, r.widthPx, c.ink,
      r.alpha * (1 - SENTRY_FADE * c.p),
    );
    disc(c, h.x, h.y, r.r2Px, COMBAT_WHITE, r.alpha2);
  },

  /**
   * Card 02, BULWARK LATTICE.
   *
   * A blunt round inside a hex lattice that turns as it flies, with a second
   * faint lattice one beat behind it and turning the other way. Armour on the
   * move, from the hull that is all armour.
   */
  'bulwark-lattice'(c) {
    const r = c.row;
    const rot = c.t * r.speed + c.k;
    const h = atOn(c.d, c.from, c.p, 0, P0);
    const b = atOn(c.d, c.from, Math.max(0, c.p - BULWARK_LAG), 0, P1);
    hexOutline(
      c, b.x, b.y, r.lengthPx, -rot, r.widthPx, c.ink,
      r.alpha2 * BULWARK_GHOST_K,
    );
    disc(c, h.x, h.y, r.rPx, c.ink, r.alpha);
    hexOutline(c, h.x, h.y, r.r2Px, rot, r.widthPx, c.ink, r.alpha2);
  },

  /**
   * Card 03, INTERCEPTOR THREAD.
   *
   * A pale needle with a dashed thread back to the tower, and the dashes walk
   * astern. Point defence: the fastest round of the page.
   *
   * The card animates `lineDashOffset`, and the segment sink has no dash
   * PHASE. So the thread walks its own dashes, one segment each, the way the
   * kit header states. `count` caps them, and past that cap the step opens so
   * the thread still reaches the tower at a range no card was drawn at.
   */
  'interceptor-thread'(c) {
    const r = c.row;
    const h = atOn(c.d, c.from, c.p, 0, P0);
    const run = c.d.len * c.p;
    const step = Math.max(THREAD_DASH_ON + THREAD_DASH_OFF, run / r.count);
    const phase = (c.t * r.driftPx) % step;
    for (let i = 0; i < r.count; i++) {
      const e = run - phase - i * step;
      if (e <= 0) break;
      const s = e - THREAD_DASH_ON;
      const e1 = e > run ? run : e;
      const s1 = s < 0 ? 0 : s;
      if (e1 <= s1) continue;
      line(
        c, c.from.x + c.d.ux * s1, c.from.y + c.d.uy * s1,
        c.from.x + c.d.ux * e1, c.from.y + c.d.uy * e1,
        r.widthPx, PALE_INK, r.alpha2,
      );
    }
    dart(c, h.x, h.y, c.d.ang, r.lengthPx, r.width2Px, PALE_INK, r.alpha);
  },

  /**
   * Card 06, WIND-UP.
   *
   * The anchor ring winds the gun. An arc fills clockwise from twelve o'clock
   * on the ring, the way the cable ring fills, and the round leaves when the
   * circle closes. The ring flashes once and goes dark. The tower reads its
   * own cadence.
   */
  'wind-up'(c) {
    const r = c.row;
    if (c.p < WINDUP_CLOSE_AT) {
      const u = c.p / WINDUP_CLOSE_AT;
      ring(c, c.from.x, c.from.y, r.r2Px, r.width2Px, c.ink, WINDUP_GUIDE_ALPHA);
      arcOf(
        c, c.from.x, c.from.y, r.r2Px, r.widthPx,
        -Math.PI / 2, TAU * u, c.ink, r.alpha2,
      );
      if (u > WINDUP_FLASH_AT) {
        flare(
          c, c.from.x, c.from.y, r.lengthPx, c.ink,
          WINDUP_FLASH_ALPHA * ((u - WINDUP_FLASH_AT) / (1 - WINDUP_FLASH_AT)),
        );
      }
      return;
    }
    const u = (c.p - WINDUP_CLOSE_AT) / (1 - WINDUP_CLOSE_AT);
    const h = atOn(c.d, c.from, u, 0, P0);
    ring(
      c, c.from.x, c.from.y, r.r2Px * (1 + WINDUP_SWELL_K * u),
      r.width2Px * WINDUP_RELEASE_W_K, c.ink, WINDUP_RELEASE_ALPHA * (1 - u),
    );
    trail(c, h, r.length2Px, r.widthPx * WINDUP_TRAIL_W_K, c.ink, WINDUP_TRAIL_ALPHA);
    disc(c, h.x, h.y, r.rPx, COMBAT_WHITE, r.alpha);
  },

  /**
   * Card 12, TRIPWIRE.
   *
   * The tower lays a wire first: a dashed line runs out to the target in the
   * first two fifths of the flight. Then a bright bead runs the wire, and the
   * wire fades behind it. A round that shows its path before it takes it.
   */
  'tripwire'(c) {
    const r = c.row;
    if (c.p < TRIPWIRE_LAY_AT) {
      const e = atOn(c.d, c.from, out(c.p / TRIPWIRE_LAY_AT), 0, P0);
      line(
        c, c.from.x, c.from.y, e.x, e.y, r.widthPx, c.ink, r.alpha2,
        TRIPWIRE_DASH_ON, TRIPWIRE_DASH_OFF,
      );
      disc(c, e.x, e.y, r.r2Px, c.ink, TRIPWIRE_TIP_ALPHA);
      return;
    }
    const u = (c.p - TRIPWIRE_LAY_AT) / (1 - TRIPWIRE_LAY_AT);
    const h = atOn(c.d, c.from, u, 0, P0);
    line(
      c, c.from.x, c.from.y, c.to.x, c.to.y, r.widthPx, c.ink,
      r.alpha2 * TRIPWIRE_RUN_K * (1 - TRIPWIRE_FADE * u),
      TRIPWIRE_DASH_ON, TRIPWIRE_DASH_OFF,
    );
    line(c, c.from.x, c.from.y, h.x, h.y, r.widthPx, COMBAT_WHITE, TRIPWIRE_LEAD_ALPHA);
    flare(c, h.x, h.y, r.lengthPx, c.ink, r.alpha2);
    disc(c, h.x, h.y, r.rPx, COMBAT_WHITE, r.alpha);
  },

  /**
   * Card 14, DRUM FIRE.
   *
   * Pairs at a steady beat. Each pair leaves as a bracket that closes on its
   * target as it flies, and a white muzzle bar flashes on every beat. The
   * tower as a metronome.
   *
   * The card fires SIX rounds at three delays, two to a delay. A salvo of the
   * map is paced by one `step_sec`, so a recipe cannot re-time a round: the
   * bracket is drawn by ONE round instead, which packs `count` heads, and the
   * row states three rounds on the card's own beat.
   */
  'drum-fire'(c) {
    const r = c.row;
    for (let i = 0; i < r.count; i++) {
      const side = i % 2 === 0 ? 1 : -1;
      const h = atOn(c.d, c.from, c.p, side * r.r2Px * (1 - c.p), P0);
      trail(c, h, r.lengthPx, r.widthPx, c.ink, r.alpha2);
      disc(c, h.x, h.y, r.rPx, COMBAT_WHITE, r.alpha);
    }
    if (c.p < DRUM_BAR_SPAN) {
      const a = 1 - c.p / DRUM_BAR_SPAN;
      line(
        c,
        c.from.x - c.d.nx * r.length2Px, c.from.y - c.d.ny * r.length2Px,
        c.from.x + c.d.nx * r.length2Px, c.from.y + c.d.ny * r.length2Px,
        r.width2Px, COMBAT_WHITE, DRUM_BAR_ALPHA * a,
      );
    }
  },

  /**
   * Card 16, STEPPED BOLT.
   *
   * The round moves in steps and not in a glide: it jumps, flashes, holds and
   * jumps again, with ghosts fading behind it on the line. A fire control that
   * reads in a paused frame as a row of dots.
   */
  'stepped-bolt'(c) {
    const r = c.row;
    const n = r.count;
    const q = Math.round(c.p * n);
    const fl = 1 - Math.abs(c.p * n - q) * 2;
    const h = atOn(c.d, c.from, q / n, 0, P0);
    for (let j = r.count2; j >= 1; j--) {
      if (q - j < 0) continue;
      const g = atOn(c.d, c.from, (q - j) / n, 0, P1);
      disc(c, g.x, g.y, r.r2Px, c.ink, BOLT_GHOST_ALPHA * (1 - j / (r.count2 + 1)));
    }
    if (q >= 1) {
      const g = atOn(c.d, c.from, (q - 1) / n, 0, P1);
      line(c, g.x, g.y, h.x, h.y, r.widthPx, c.ink, BOLT_LINK_ALPHA);
    }
    flare(c, h.x, h.y, r.lengthPx * fl, c.ink, r.alpha2 * fl);
    disc(c, h.x, h.y, r.rPx, COMBAT_WHITE, r.alpha);
  },

  /**
   * Card 17, MINE DRIFT.
   *
   * A spiked mine drifts to the target on a wobbling path, spins slowly and
   * blinks, with a dark shadow under it. The slowest round of the page: the
   * tower seeds the water it holds.
   */
  'mine-drift'(c) {
    const r = c.row;
    const glide = MINE_EASE_K * out(c.p) + (1 - MINE_EASE_K) * c.p;
    const off = Math.sin(c.p * TAU * MINE_WOBBLE_TURNS + c.k)
      * r.length2Px * Math.sin(Math.PI * c.p);
    const h = atOn(c.d, c.from, glide, off, P0);
    const rot = c.t * r.speed + c.k;
    blob(
      c, h.x + r.rPx * MINE_SHADOW_DX_K, h.y + r.rPx * MINE_SHADOW_DY_K,
      r.rPx * MINE_SHADOW_RX_K, r.rPx * MINE_SHADOW_RY_K, 0,
      DEEP_INK, MINE_SHADOW_ALPHA,
    );
    for (let i = 0; i < r.count; i++) {
      const a = rot + (i / r.count) * TAU;
      const cs = Math.cos(a), sn = Math.sin(a);
      line(
        c, h.x + cs * r.r2Px, h.y + sn * r.r2Px,
        h.x + cs * r.lengthPx, h.y + sn * r.lengthPx,
        r.widthPx, c.ink, r.alpha2,
      );
    }
    disc(c, h.x, h.y, r.rPx, DEEP_INK, r.alpha);
    ring(c, h.x, h.y, r.rPx, r.width2Px, c.ink, r.alpha2);
    disc(
      c, h.x, h.y, r.rPx * MINE_PIP_K, COMBAT_WHITE,
      MINE_BLINK_REST + MINE_BLINK_RISE * (0.5 + 0.5 * Math.sin(c.t * MINE_BLINK_RATE)),
    );
  },

  /**
   * Card 20, SEA-BED KICK.
   *
   * The planted tower kicks the sea bed when it fires: a puff of silt stands
   * up under the hull and grains rise out of it, while a heavy bead with a
   * white core crosses. The recoil of a hull that cannot move.
   *
   * The card scatters its grains off a hash of its own card number, so every
   * frame of the page drew one puff. Here the scatter reads `c.seed`: a map
   * holds many towers, and one shared silt shape would read as a pattern.
   */
  'sea-bed-kick'(c) {
    const r = c.row;
    const h = atOn(c.d, c.from, c.p, 0, P0);
    if (c.p < KICK_PUFF_SPAN) {
      const u = c.p / KICK_PUFF_SPAN;
      // The silt stands where the hull is planted: astern of the centre down
      // the line of fire, and below it.
      const bx = c.from.x - c.d.ux * r.width2Px;
      const by = c.from.y + r.width2Px;
      blob(
        c, bx, by, r.length2Px * (1 + KICK_PUFF_GROW_X_K * u),
        r.length2Px * KICK_PUFF_RY_K * (1 + u), 0,
        NEUTRAL_INK, r.alpha2 * (1 - u),
      );
      for (let i = 0; i < r.count; i++) {
        const g = hash(c.seed, i + 1);
        disc(
          c, bx + (g - 0.5) * r.length2Px * KICK_GRAIN_SPREAD_K,
          by - u * r.length2Px * KICK_GRAIN_RISE_K * (0.5 + g),
          KICK_GRAIN_R + g * KICK_GRAIN_R_SPAN, NEUTRAL_INK,
          KICK_GRAIN_ALPHA * (1 - u),
        );
      }
    }
    trail(c, h, r.lengthPx, r.widthPx, c.ink, KICK_TRAIL_ALPHA);
    disc(c, h.x, h.y, r.rPx, c.ink, r.alpha);
    disc(c, h.x - c.d.ux, h.y - c.d.uy, r.r2Px, COMBAT_WHITE, KICK_CORE_ALPHA);
  },
};

/**
 * The cards of round 050 that wait for their own recipe.
 *
 * The table is EMPTY: every id of `labels.toml shot_sentinel_styles` has its
 * own recipe above and its own row in `data/presentation/combat.toml`. The
 * mockup page `archive/mockups/050-shots-sentinel.html` is the specification
 * of each, and it is a record: nobody edits it.
 */
export const PENDING: readonly string[] = [];
